import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import productsService from "../../services/products";

import BackdropLoading from "../commons/BackdropLoading";
import EditControllers from "../commons/EditControllers";
import Form from "../commons/Form";
import FormFields from "../commons/FormFields";
import { SaveIcon } from "../commons/Icons";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormField from "../commons/FormField";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";

class EditProductStock extends React.Component {
  constructor(props) {
    super(props);
    this._isMounted = false; // isMounted React pattern to avoid memory leaks
    this.state = {
      loading: false,
      data: null,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    const { product } = this.props;
    if (product) {
      // le produit est passé en props
      this._isMounted &&
        this.setState({
          data: {
            state: product?.state || "",
            sku: product?.sku || "",
            stock_enabled: product?.stock_enabled ? true : false,
            stock_qty: product?.stock_qty != null ? product.stock_qty : "",
            stock_allow_backorder: product?.stock_allow_backorder ? true : false,
            stock_alert_threshold: product?.stock_alert_threshold != null ? product.stock_alert_threshold : "",
          },
        });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  showLoading() {
    this._isMounted && this.setState({ loading: true });
  }

  hideLoading() {
    this._isMounted && this.setState({ loading: false });
  }

  save(e) {
    e.preventDefault();
    const { data } = this.state;
    const { id_profile, product } = this.props;
    const parsed_data = {
      state: data.state,
      sku: data.sku,
      stock_enabled: data.stock_enabled ? 1 : 0,
      stock_qty: data.stock_enabled && data.stock_qty !== "" ? parseFloat(data.stock_qty) : null,
      stock_allow_backorder: data.stock_enabled && data.stock_allow_backorder ? 1 : 0,
      stock_alert_threshold:
        data.stock_enabled && data.stock_alert_threshold !== "" ? parseFloat(data.stock_alert_threshold) : null,
    };
    this.showLoading();
    productsService
      .update(id_profile, product.ID, parsed_data)
      .then(([id, notices]) => {
        notices && notices.length > 0 && this.props.onError(notices);
        // récupération de l'item à jour
        return productsService.get(id_profile, id);
      })
      .then(([product]) => {
        this.props.onSaved(product, this.state.is_new);
      })
      .catch((error) => {
        console.error("error : ", error);
        this.props.onError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
    return false;
  }

  render() {
    const { loading, data } = this.state;
    const { classes, id_profile, product } = this.props;
    if (!id_profile) {
      return (
        <Typography className={classes.error} component="p">
          Aucun profil de passé en paramètre.
        </Typography>
      );
    }
    if (!product) {
      return (
        <Typography className={classes.error} component="p">
          Aucun produit de passé en paramètre.
        </Typography>
      );
    }

    return (
      <Box className={classes.container}>
        <BackdropLoading open={loading} />

        {data && (
          <Form onSubmit={(e) => this.save(e)} noValidate>
            <FormFields className={classes.formFields}>
              <FormField>
                <FormControl className={classes.form_control}>
                  <InputLabel id="select-label-state">Disponibilité</InputLabel>
                  <Select
                    labelId="select-label-state"
                    label="Disponibilité"
                    id="state"
                    value={
                      data.state === productsService.META_STATE_OUTOFSTOCK
                        ? productsService.META_STATE_OUTOFSTOCK
                        : data.state
                    }
                    onChange={(e) => this.setState({ data: { ...data, ...{ state: e.target.value } } })}
                    disabled={data.state === productsService.META_STATE_OUTOFSTOCK}
                  >
                    <MenuItem value={productsService.META_STATE_AVAILABLE}>Disponible</MenuItem>
                    <MenuItem value={productsService.META_STATE_UNAVAILABLE}>Indisponible</MenuItem>
                    {data.state === productsService.META_STATE_OUTOFSTOCK && (
                      <MenuItem value={productsService.META_STATE_OUTOFSTOCK} disabled>
                        Rupture de stock
                      </MenuItem>
                    )}
                  </Select>
                  {data.state === productsService.META_STATE_OUTOFSTOCK && (
                    <FormHelperText>Géré automatiquement selon le stock</FormHelperText>
                  )}
                </FormControl>
              </FormField>

              <FormField>
                <FormControl className={classes.form_control}>
                  <TextField
                    id="sku"
                    label="UGS/SKU"
                    type="text"
                    className={classes.input}
                    value={data.sku}
                    onChange={(e) => this.setState({ data: { ...data, ...{ sku: e.target.value } } })}
                  />
                  <FormHelperText>Unité de gestion de stock</FormHelperText>
                </FormControl>
              </FormField>

              <FormField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={data.stock_enabled}
                      onChange={(e) => this.setState({ data: { ...data, stock_enabled: e.target.checked } })}
                      color="primary"
                    />
                  }
                  label="Activer le suivi des quantités"
                />
              </FormField>

              {data.stock_enabled && (
                <>
                  <FormField>
                    <FormControl className={classes.form_control}>
                      <TextField
                        id="stock_qty"
                        label="Quantité en stock"
                        type="number"
                        inputProps={{ step: "0.001", min: "0" }}
                        className={classes.input}
                        value={data.stock_qty}
                        InputProps={
                          product.format === productsService.META_FORMAT_BULK && product.volume_unit
                            ? { endAdornment: <InputAdornment position="end">{product.volume_unit}</InputAdornment> }
                            : undefined
                        }
                        onChange={(e) => this.setState({ data: { ...data, stock_qty: e.target.value } })}
                      />
                    </FormControl>
                  </FormField>

                  <FormField>
                    <FormControl className={classes.form_control}>
                      <TextField
                        id="stock_alert_threshold"
                        label="Seuil d'alerte"
                        type="number"
                        inputProps={{ step: "0.001", min: "0" }}
                        className={classes.input}
                        value={data.stock_alert_threshold}
                        InputProps={
                          product.format === productsService.META_FORMAT_BULK && product.volume_unit
                            ? { endAdornment: <InputAdornment position="end">{product.volume_unit}</InputAdornment> }
                            : undefined
                        }
                        onChange={(e) => this.setState({ data: { ...data, stock_alert_threshold: e.target.value } })}
                      />
                      <FormHelperText>Vous serez notifié(e) lorsque le stock atteint ce seuil</FormHelperText>
                    </FormControl>
                  </FormField>

                  <FormField>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={data.stock_allow_backorder}
                          onChange={(e) => this.setState({ data: { ...data, stock_allow_backorder: e.target.checked } })}
                          color="primary"
                        />
                      }
                      label="Accepter les commandes même en rupture de stock"
                    />
                  </FormField>
                </>
              )}
            </FormFields>

            <EditControllers sticky>
              <Button
                type="submit"
                className={classes.submit}
                onClick={(e) => this.save(e)}
                color="primary"
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Enregistrer
              </Button>
            </EditControllers>
          </Form>
        )}
      </Box>
    );
  }
}

const styles = (theme) => ({
  container: {
    paddingTop: theme.spacing(1),
  },
  form_control: {
    width: "100%",
  },
  input: {
    width: "100%",
  },
  submit: {
    width: "100%",
  },
  formFields: {
    padding: theme.spacing(0, 2),
  },
});

EditProductStock.propTypes = {
  id_profile: PropTypes.PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  product: PropTypes.object.isRequired, // produit à modifier
  onSaved: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default withStyles(styles, { withTheme: true })(EditProductStock);
